// NeoCore · Verificaciones de Analista de Cumplimiento
// -----------------------------------------------------
// Módulo del analista/oficial de cumplimiento. Centraliza el trabajo que los
// instructivos de la IVE piden sobre (1) Conoce a tu Cliente (FEIC/KYC) y
// (2) Monitoreo Transaccional (RTS), más las áreas de listas restrictivas,
// PEP/CPE, beneficiarios finales y medidas ampliadas.
//
// v1: engancha la data real que ya existe (banderas de OCR del recibo y del
// comprobante, discrepancias de MRZ, recibos vencidos >3m, marcas PEP del FEIS)
// y deja las áreas de screening de listas / medidas ampliadas como cola a poblar
// con los instructivos correspondientes.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  ArrowsClockwise, Fingerprint, ShieldWarning, Scales, UserFocus,
  Warning, ListChecks, Clock,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

// ---- Helpers ----------------------------------------------------------------

/** Ejecuta una query y degrada a [] ante cualquier error (no rompe la página). */
async function safe<T>(fn: () => Promise<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  try {
    const { data } = await fn()
    return data ?? []
  } catch {
    return []
  }
}

function mesesDesde(iso: string | null): number | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`)
  if (isNaN(d.getTime())) return null
  const now = new Date()
  if (d.getTime() > now.getTime()) return -1 // futura = sospechosa
  let m = (now.getUTCFullYear() - d.getUTCFullYear()) * 12 + (now.getUTCMonth() - d.getUTCMonth())
  if (now.getUTCDate() < d.getUTCDate()) m -= 1
  return Math.max(0, m)
}

type Aso = { no_asociado?: number | null; neo_id?: string | null; nombre_completo?: string | null; dpi?: string | null }

type KycRow = {
  id: string
  estado: string | null
  recibo_servicios_fecha: string | null
  recibo_servicios_tipo: string | null
  datos_extraidos: any
  asociado: Aso | Aso[] | null
}
type RepRow = {
  id: string; monto: number | null; estado: string | null
  notas_asociado: string | null; created_at: string; asociado: Aso | Aso[] | null
}

function unwrap(a: Aso | Aso[] | null): Aso {
  if (!a) return {}
  return Array.isArray(a) ? (a[0] ?? {}) : a
}
function nombre(a: Aso | Aso[] | null): string {
  const x = unwrap(a)
  return x.nombre_completo || (x.neo_id ?? '') || (x.no_asociado != null ? `#${x.no_asociado}` : '—')
}

// ---- Página -----------------------------------------------------------------

export function NeoCoreVerificacionesAnalistaPage() {
  const [loading, setLoading] = useState(true)
  const [kyc, setKyc] = useState<KycRow[]>([])
  const [reps, setReps] = useState<RepRow[]>([])
  const [pep, setPep] = useState<number>(0)

  const load = useCallback(async () => {
    setLoading(true)
    const [kycRows, repRows, pepRows] = await Promise.all([
      safe<KycRow>(() => supabase
        .from('kyc_verification')
        .select('id, estado, recibo_servicios_fecha, recibo_servicios_tipo, datos_extraidos, asociado:asociado_id(no_asociado, neo_id, nombre_completo, dpi)')
        .order('created_at', { ascending: false })
        .limit(400) as any),
      safe<RepRow>(() => supabase
        .from('reporte_pago_asociado')
        .select('id, monto, estado, notas_asociado, created_at, asociado:asociado_id(no_asociado, neo_id, nombre_completo)')
        .order('created_at', { ascending: false })
        .limit(300) as any),
      safe<any>(() => supabase
        .from('feis_expediente')
        .select('asociado_id, es_pep, es_cpe, es_familiar_pep')
        .or('es_pep.eq.true,es_cpe.eq.true,es_familiar_pep.eq.true') as any),
    ])
    setKyc(kycRows)
    setReps(repRows)
    setPep(pepRows.length)
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  // -- Cola KYC / Conoce a tu Cliente: filas con alguna bandera ---------------
  const kycFlags = useMemo(() => {
    return kyc.map((r) => {
      const disc: Array<{ campo: string; mensaje: string }> = Array.isArray(r.datos_extraidos?.discrepancias)
        ? r.datos_extraidos.discrepancias : []
      const meses = mesesDesde(r.recibo_servicios_fecha)
      const banderas: string[] = []
      for (const d of disc) banderas.push(d.mensaje || d.campo)
      if (meses === -1) banderas.push('Recibo con fecha futura (posible error de lectura).')
      else if (meses != null && meses > 3) banderas.push(`Recibo con ${meses} meses de antigüedad (máx. 3).`)
      else if (r.recibo_servicios_fecha == null) banderas.push('Sin fecha de recibo legible.')
      return { ...r, banderas }
    }).filter((r) => r.banderas.length > 0)
  }, [kyc])

  // -- Cola de monitoreo transaccional: OCR marcado o pendiente ---------------
  const repFlags = useMemo(() => {
    return reps.filter((r) =>
      (r.notas_asociado && /REVISAR/i.test(r.notas_asociado)) || r.estado === 'pendiente',
    )
  }, [reps])

  const areas = [
    { key: 'kyc', Icon: Fingerprint, titulo: 'Conoce a tu Cliente (FEIC/KYC)', count: kycFlags.length, ref: 'FEIC · Oficio IVE 2358-2022', tone: '#0e7490' },
    { key: 'rts', Icon: ShieldWarning, titulo: 'Monitoreo Transaccional (RTS)', count: repFlags.length, ref: 'RTS · Oficio IVE 4164-2025', tone: '#b45309' },
    { key: 'pep', Icon: UserFocus, titulo: 'Personas Expuestas (PEP/CPE)', count: pep, ref: 'PEP · Oficio IVE 1033-2013', tone: '#7c3aed' },
    { key: 'listas', Icon: Scales, titulo: 'Listas restrictivas (ONU/OFAC/GAFI)', count: null, ref: 'Screening pendiente de instructivo', tone: '#64748b' },
    { key: 'bf', Icon: ListChecks, titulo: 'Beneficiarios Finales / Medidas Ampliadas', count: null, ref: 'Oficio IVE 813-2023 · 477-2024', tone: '#64748b' },
  ]

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Verificaciones de Analista de Cumplimiento</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Monitoreo transaccional y Conoce a tu Cliente según los instructivos de la IVE. Las banderas se
            generan automáticamente; el analista revisa y resuelve.
          </p>
        </div>
        <button onClick={() => void load()} className="btn btn-ghost">
          <ArrowsClockwise size={13} weight="bold" /> Refrescar
        </button>
      </div>

      {/* Áreas de cumplimiento */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12, marginBottom: 24 }}>
        {areas.map((a) => (
          <div key={a.key} style={{ border: '1px solid var(--hairline)', borderRadius: 12, padding: 14, background: 'var(--surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <a.Icon size={18} weight="duotone" color={a.tone} />
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>{a.titulo}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: a.count && a.count > 0 ? a.tone : 'var(--muted-foreground)' }}>
              {a.count == null ? '—' : a.count}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 2 }}>{a.ref}</div>
          </div>
        ))}
      </div>

      {loading && <p style={{ color: 'var(--muted-foreground)', fontSize: 13 }}>Cargando…</p>}

      {/* Cola: Conoce a tu Cliente */}
      <Section titulo="Conoce a tu Cliente · KYC con banderas" icon={<Fingerprint size={16} weight="bold" />}>
        {kycFlags.length === 0 ? (
          <Vacio texto="Sin banderas de KYC pendientes." />
        ) : (
          <Tabla headers={['Asociado', 'DPI', 'Recibo', 'Banderas']}>
            {kycFlags.map((r) => {
              const a = unwrap(r.asociado)
              return (
                <tr key={r.id} style={{ borderTop: '1px solid var(--hairline)' }}>
                  <Td><b>{nombre(r.asociado)}</b></Td>
                  <Td mono>{a.dpi ?? '—'}</Td>
                  <Td>{r.recibo_servicios_tipo || '—'}{r.recibo_servicios_fecha ? ` · ${r.recibo_servicios_fecha}` : ''}</Td>
                  <Td>
                    {r.banderas.map((b, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 7px', fontSize: 11, marginRight: 4, marginBottom: 3 }}>
                        <Warning size={11} weight="fill" /> {b}
                      </span>
                    ))}
                  </Td>
                </tr>
              )
            })}
          </Tabla>
        )}
      </Section>

      {/* Cola: Monitoreo transaccional */}
      <Section titulo="Monitoreo Transaccional · reportes a revisar" icon={<ShieldWarning size={16} weight="bold" />}>
        {repFlags.length === 0 ? (
          <Vacio texto="Sin reportes marcados para revisión." />
        ) : (
          <Tabla headers={['Asociado', 'Monto', 'Estado', 'Fecha', 'Nota / bandera']}>
            {repFlags.map((r) => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--hairline)' }}>
                <Td><b>{nombre(r.asociado)}</b></Td>
                <Td mono>Q{Number(r.monto ?? 0).toLocaleString('es-GT')}</Td>
                <Td>{r.estado}</Td>
                <Td><Clock size={11} weight="bold" /> {r.created_at?.slice(0, 10)}</Td>
                <Td>
                  {r.notas_asociado && /REVISAR/i.test(r.notas_asociado)
                    ? <span style={{ color: '#b45309', fontSize: 12 }}>{r.notas_asociado}</span>
                    : <span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>{r.notas_asociado || 'Pendiente de conciliar'}</span>}
                </Td>
              </tr>
            ))}
          </Tabla>
        )}
      </Section>
    </div>
  )
}

// ---- Sub-componentes --------------------------------------------------------

function Section({ titulo, icon, children }: { titulo: string; icon: ReactNode; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        {icon}<h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{titulo}</h2>
      </div>
      {children}
    </div>
  )
}
function Tabla({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden', background: 'var(--surface)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--surface-2, #f8fafc)' }}>
            {headers.map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, color: 'var(--muted-foreground)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}
function Td({ children, mono }: { children: ReactNode; mono?: boolean }) {
  return <td style={{ padding: '9px 12px', fontFamily: mono ? 'ui-monospace, monospace' : undefined, verticalAlign: 'top' }}>{children}</td>
}
function Vacio({ texto }: { texto: string }) {
  return <div style={{ border: '1px dashed var(--hairline)', borderRadius: 12, padding: 18, color: 'var(--muted-foreground)', fontSize: 13 }}>{texto}</div>
}
